import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';

export const FilesTab = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchFiles(); }, []);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/files`);
      setFiles(res.data.files || []);
    } catch (error) { toast.error('Failed to load files'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="luxury-card p-6">
        <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Uploaded Files</h3>
        <p className="text-slate-500 text-sm mb-1">{files.length} files stored</p>
        <p className="text-amber-600 text-xs">Note: Files are stored locally and will be lost on redeployment. Cloud storage migration is recommended.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {files.map((file) => (
          <a key={file.filename} href={`${API_URL}${file.url}`} target="_blank" rel="noopener noreferrer"
            className="luxury-card p-3 hover:shadow-md transition-shadow group" data-testid={`file-${file.filename}`}>
            {['jpg', 'jpeg', 'png', 'webp'].includes(file.type) ? (
              <img src={`${API_URL}${file.url}`} alt={file.filename} className="w-full h-24 object-cover mb-2 bg-slate-100" loading="lazy" />
            ) : (
              <div className="w-full h-24 bg-slate-100 flex items-center justify-center mb-2">
                <span className="text-slate-400 text-xs uppercase">{file.type}</span>
              </div>
            )}
            <p className="text-xs text-slate-600 truncate">{file.filename}</p>
            <p className="text-xs text-slate-400">{file.size_kb} KB</p>
          </a>
        ))}
      </div>
      {files.length === 0 && <div className="luxury-card p-10 text-center text-slate-500">No files uploaded yet</div>}
    </div>
  );
};

export const ReferralsTab = ({ referrals, onRefresh }) => {
  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/referrals/${id}/status`, { status });
      toast.success('Status updated');
      onRefresh();
    } catch (error) { toast.error('Failed to update'); }
  };

  const statusColors = {
    pending: 'bg-amber-950/95 text-amber-300 border-amber-500/50',
    contacted: 'bg-blue-950/95 text-blue-300 border-blue-500/50',
    completed: 'bg-emerald-950/95 text-emerald-300 border-emerald-500/50',
  };

  return (
    <div className="space-y-4" data-testid="referrals-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Professional Referral Requests</h2>
        <p className="text-sm text-slate-500">{referrals.length} total</p>
      </div>
      {referrals.length === 0 ? (
        <div className="luxury-card p-8 text-center text-slate-400">No referral requests yet</div>
      ) : (
        referrals.map((r) => (
          <div key={r.id} className="luxury-card p-5" data-testid={`referral-item-${r.id}`}>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#1A1A1A]">{r.contact_name}</span>
                  <span className={`text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider rounded border ${statusColors[r.status] || 'bg-slate-900 text-slate-300 border-slate-500/40'}`}>
                    {r.status}
                  </span>
                </div>
                <p className="text-sm text-slate-500">Needs: <span className="text-[#E07A3A] font-semibold capitalize">{r.service_type}</span></p>
                <p className="text-xs text-slate-400">Requested by: {r.user_name} ({r.user_email})</p>
                {r.contact_email && <p className="text-xs text-slate-400">Email: {r.contact_email}</p>}
                {r.contact_phone && <p className="text-xs text-slate-400">Phone: {r.contact_phone}</p>}
                {r.message && <p className="text-sm text-slate-600 mt-1 italic">"{r.message}"</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {r.status === 'pending' && (
                  <Button size="sm" onClick={() => updateStatus(r.id, 'contacted')} className="bg-blue-500 text-white hover:bg-blue-600 text-xs" data-testid={`referral-contact-${r.id}`}>
                    Mark Contacted
                  </Button>
                )}
                {r.status !== 'completed' && (
                  <Button size="sm" onClick={() => updateStatus(r.id, 'completed')} className="bg-green-500 text-white hover:bg-green-600 text-xs" data-testid={`referral-complete-${r.id}`}>
                    Complete
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};
